import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { coreFetchCustomers } from '../../../Reducers/core';
import useBoolean from '../../../Utils/useBoolean';
import OrderKeysModal from '../OrderKeysModal';
import { ShoppingCartOutlined } from '@ant-design/icons';
import {
  getCurrentCustomerFromLSOrNull,
  isValidCurrentCustomer,
} from '../../../Services/CoreService';
import { CreditCardOutlined, CreditCardRounded, CreditCardSharp, Payments } from '@mui/icons-material';

const initalValue = {
  countKey: 1,
};

function OrderButton({
  pltId,
  productId,
  region,
  defaultMaxPrice,
  game,
  prices,
  stateReducer,
  orderGames,
  orderGamesNonMgr,
  orderGamesReset,
  getQuantityOnStock,
  isUserAllowed,
  activeCurrency,
  activeCurrencySymbol
}) {
  const dispatch = useDispatch();

  const { value: visible, setTrue: open, setFalse: close } = useBoolean(false);
  const [number, setNumber] = useState(initalValue.countKey);
  const [maxPrice, setMaxPrice] = useState(defaultMaxPrice);
  const [orderID, setOrderID] = useState('');
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = useCallback(() => {
    if (!submitting.current) close();
  }, [close]);

  const stockAmount = useMemo(
    () => prices.reduce((acc, cur) => (cur.amount || 0) + acc, 0),
    [prices]
  );

  const changeOrderID = useCallback(
    (e) => {
      setOrderID(e.target.value);
    },
    [setOrderID]
  );

  const {
    orderGamesLoading,
    currentCustomerFromState,
    nonManagerCustomerInit,
  } = useSelector(
    (state) => ({
      orderGamesLoading: stateReducer(state).order_games.isLoading,
      currentCustomerFromState: state.core.customer,
      nonManagerCustomerInit: state.core.nonManagerCustomerInit,
    }),
    shallowEqual
  );

  const currentCustomer = useMemo(
    () => (
      isValidCurrentCustomer(currentCustomerFromState)
        ? currentCustomerFromState
        : isUserAllowed
          ? getCurrentCustomerFromLSOrNull()
          : null
    ),
    [currentCustomerFromState, isUserAllowed]
  );

  const customerParams = currentCustomer?.params || {};
  const hasBuyFromStockSetting =
    customerParams.buyFromStock !== undefined && customerParams.buyFromStock !== null;
  const hasCustomerParams = Object.keys(customerParams).length > 0;
  const missingNonManagerCustomerParams =
    !isUserAllowed
    && nonManagerCustomerInit.isInitialized
    && !hasCustomerParams;

  useEffect(() => {
    if (missingNonManagerCustomerParams) {
      console.error('OrderButton rendered without valid non-manager customer params.', currentCustomer);
    }
  }, [currentCustomer, missingNonManagerCustomerParams]);

  const buyFromStockSetting = customerParams.buyFromStock ?? false
  const skipOrderPriceValidation = customerParams.skipOrderPriceValidation ?? false
  const maxOnDemandKeys = customerParams.maxOnDemandKeys ?? 0
  
  const {
    value: buyFromStock,
    setValue: setBuyFromStock,
    toggleValue: toggleBuyFromStock,
  } = useBoolean(buyFromStockSetting);
  
  const changeBuyFromStock = useCallback(() => {
    toggleBuyFromStock();
  }, [toggleBuyFromStock]);

  useEffect(() => {
    setBuyFromStock(stockAmount === 0 ? false : buyFromStockSetting);
  }, [buyFromStockSetting, setBuyFromStock, stockAmount]);

  useEffect(() => {
    setMaxPrice(defaultMaxPrice);
  }, [defaultMaxPrice]);

  const {
    value: immediate,
    toggleValue: toggleImmediate,
    setValue: setImmediate,
  } = useBoolean(maxOnDemandKeys >= number);

  const {
    value: isTouchedImmediate,
    setTrue: touchImmediate,
    setFalse: disableTouch,
  } = useBoolean(false);

  const changeImmediate = useCallback(() => {
    toggleImmediate();
    touchImmediate();
  }, [touchImmediate, toggleImmediate]);

  useEffect(() => {
    if (!isTouchedImmediate) {
      setImmediate(maxOnDemandKeys >= number);
    }
    if (!visible) {
      setNumber(initalValue.countKey);
      setOrderID('');
      disableTouch();
    }
  }, [disableTouch, isTouchedImmediate, visible, maxOnDemandKeys, number, setImmediate]);

  const [quantityOnStock, setQuantityOnStock] = useState('-');

  const handleOnClick = () => {
    dispatch(getQuantityOnStock(pltId, productId, region))
      .then((res) => setQuantityOnStock(res.qty))
      .catch((error) => {
        setQuantityOnStock('-');
        message.error(error?.errors?.[0]?.message || error?.message
          || (typeof error === 'string' ? error : 'Could not load stock quantity.'));
      });

    open() 
  }
  const onOk = useCallback(async () => {
    // A ref closes the same-tick gap before React updates loading props.
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      const resp = await dispatch(
        isUserAllowed ? orderGames(
          pltId,
          [`${productId}:${region}`],
          number,
          typeof maxPrice === 'number' ? maxPrice.toFixed(2) : maxPrice,
          orderID,
          immediate,
          buyFromStock
        ) : orderGamesNonMgr(
          activeCurrency,
          pltId,
          [`${productId}:${region}`],
          number,
          typeof maxPrice === 'number' ? maxPrice.toFixed(2) : maxPrice,
          orderID
        )
      );
      close();
      setNumber(initalValue.countKey);
      const orderNumber = resp && resp.orderId ? ` #${resp.orderId}` : '';
      message.success(
        `The successful creation of the order${orderNumber}! Open the Ordered page for more information.`
      );
      dispatch(coreFetchCustomers()).catch(() => {
        message.error('Order created, but customer details could not be refreshed.');
      });
    } catch (error) {
      message.error(error?.errors?.[0]?.message || error?.message
        || (typeof error === 'string' ? error : 'Order creation failed.'));
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  }, [dispatch, isUserAllowed, orderGames, orderGamesNonMgr, pltId, activeCurrency,
    productId, region, number, close, maxPrice, orderID, immediate, buyFromStock]);

  const disableBuyFromStock = useMemo(() => hasBuyFromStockSetting, [hasBuyFromStockSetting])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => dispatch(orderGamesReset()), []);
  return (
    <>
      <Tooltip title={missingNonManagerCustomerParams ? 'Customer params unavailable' : 'Order Keys'}>
        <Button
          type="primary"
          size="small"
          icon={<Payments style={{ width: 15 }} />}
          onClick={handleOnClick}
          disabled={!productId || missingNonManagerCustomerParams}
          loading={isSubmitting || orderGamesLoading}
        />
      </Tooltip>
      <OrderKeysModal
        loading={isSubmitting || orderGamesLoading}
        visible={visible}
        immediate={immediate}
        changeImmediate={changeImmediate}
        close={closeModal}
        setNumber={setNumber}
        number={number}
        onOk={onOk}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        quantityOnStock={quantityOnStock}
        buyFromStock={buyFromStock}
        changeBuyFromStock={changeBuyFromStock}
        buyFromStockSetting={buyFromStockSetting}
        disableBuyFromStock={disableBuyFromStock}
        skipOrderPriceValidation={skipOrderPriceValidation}
        stockAmount={stockAmount}
        game={game}
        region={region}
        changeOrderID={changeOrderID}
        orderID={orderID}
        pltId={pltId}
        isUserAllowed={isUserAllowed}
        activeCurrencySymbol={activeCurrencySymbol}
      />
    </>
  );
}

OrderButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  pltId: PropTypes.number,
  productId: PropTypes.string.isRequired,
  defaultMaxPrice: PropTypes.number,
  game: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  prices: PropTypes.any.isRequired,
  stateReducer: PropTypes.func.isRequired,
  orderGames: PropTypes.func.isRequired,
  orderGamesReset: PropTypes.func.isRequired,
};

OrderButton.defaultProps = {
  defaultMaxPrice: 0,
};

export default OrderButton;
